import { Reveal, Label } from "./Reveal";
import { Thread } from "./Thread";

const CHAIN = [
  "Sometimes we\u2019ll start talking about bedtime \u2014 and realize the thing I want to look at actually happened hours earlier.",
  "Maybe a nap ran short. Maybe the afternoon shifted. Maybe bedtime moved.",
  "That\u2019s why I don\u2019t want to look at one difficult moment and guess. I want to understand the whole day.",
];

const REPLAY = [
  "Was the nap too short?",
  "Was bedtime too early?",
  "Is the schedule wrong?",
  "Is this a regression?",
  "Should we start sleep training?",
  "Should we stop sleep training?",
];

export const WhySimple = () => (
  <section id="why-not-simple" data-testid="why-not-simple-section" className="bg-ivory">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-6 lg:pt-10">
      <Thread className="text-clay/40" flip />
    </div>

    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <Label>Why nothing seems simple</Label>
          </Reveal>
          <Reveal as="h2" delay={0.08} className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
            The hardest sleep problems
            <span className="block">rarely look like</span>
            <span className="block italic text-clay">one problem.</span>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="mt-10 space-y-3.5 max-w-md">
              {CHAIN.map((c) => (
                <p key={c} className="flex items-baseline gap-4 text-base sm:text-lg font-normal text-smoke">
                  <span aria-hidden="true" className="h-px w-5 shrink-0 bg-clay translate-y-[-5px]" />
                  {c}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            <p data-testid="why-not-simple-distinction" className="mt-9 font-serif italic font-light text-xl sm:text-2xl leading-snug text-ink/70 max-w-md">
              A baby who wakes during every crib transfer and a toddler who
              keeps coming out of their room both get called &ldquo;a bedtime
              problem.&rdquo;
            </p>
            <p className="mt-4 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-md">
              They usually don&rsquo;t need the same answer. That&rsquo;s the
              part worth figuring out first.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal>
          <p className="text-base sm:text-lg font-normal text-smoke">
            And by breakfast, you&rsquo;re replaying the whole thing:
          </p>
          <div className="mt-6 space-y-3 font-serif italic font-light text-xl sm:text-2xl leading-snug text-ink/70">
            {REPLAY.map((q) => (
              <p key={q}>{q}</p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <p className="mt-14 font-serif font-light text-ink text-3xl sm:text-4xl lg:text-[2.6rem] leading-[1.15] tracking-tight">
            When you don&rsquo;t know
            <span className="block">which part matters,</span>
            <span className="block italic text-clay mt-2">everything starts</span>
            <span className="block italic text-clay">looking like the problem.</span>
          </p>
          <p className="mt-8 max-w-lg text-base sm:text-lg font-normal leading-relaxed text-smoke">
            That&rsquo;s how you end up changing five things while trying to fix
            one.
          </p>
        </Reveal>

        <Reveal delay={0.18}>
          <p className="mt-20 lg:mt-28 font-serif font-light text-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight">
            You don&rsquo;t need
            <span className="block">more things to try.</span>
            <span className="block mt-6 italic text-clay">We&rsquo;ll figure out</span>
            <span className="block italic text-clay">which part</span>
            <span className="block italic text-clay">matters.</span>
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
